import { useEffect, useMemo, useRef, useState } from 'react'
import {
  X, Loader2, Stethoscope, Phone, Landmark, Ban, FileText, MessageSquare,
  Lock, Pencil, Camera, Plus, Trash2, Upload, Check, Power, GripVertical,
  ChevronUp, ChevronDown, AlertTriangle, MailCheck,
} from 'lucide-react'
import { apiGet, cachedGet, patchJson, postJson, invalidateCache } from '../../lib/api.js'
import { nameInitial } from '../../lib/text.js'
import { fmtDateTH } from '../../lib/asset.js'
import { useMe } from '../../lib/me.js'
import { canViewSalary, isAdmin as isAdminRole, canEditDentist } from '../../lib/role.js'
import SearchableSelect from '../SearchableSelect.jsx'
import ConfirmDialog from '../ConfirmDialog.jsx'
import {
  activeInfo, specList, specLabel, doctorCode, doctorFullName, exceptList, exceptIds,
  maskTail, indexAppointments, specChoices, exceptChoices, pruneExcept, buildCode,
  BANKS, CASH_RECEIVE_OPTIONS, DEFAULT_CASH_OPTION, isMirroredBank, emptyBank, normBank, bankPatch,
  dentistFileHref, fileTagText, FILE_TAGS, fmtBytes, isImageFile,
} from '../../lib/dentist.js'
import { generateThumbnail, blobToDataURL } from '../../lib/thumbnail.js'
import { uploadFile } from '../../lib/uploadDirect.js'

const INPUT = 'w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[13.5px] text-slate-800 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10 disabled:bg-slate-50 disabled:text-slate-400'

/**
 * One doctor — detail + edit drawer, opened from Manager › ทันตแพทย์.
 *
 * The list is served projected (no PII, no blobs), so the FULL record is fetched
 * here one doctor at a time. Every write goes through `PATCH /api/doctors/:id`,
 * which forwards only the keys present in the body — so each section saves what
 * it touched and nothing else.
 *
 * Roles: Manager/Admin may edit (the proxy enforces the same). `email` is the
 * doctor-portal login identity → Admin only. `idCard`/`address` are HR/Admin
 * (masked for everyone else, and read-only for them — you cannot overwrite what
 * you cannot see).
 */
export default function DentistDrawer({ row, onClose, onSaved }) {
  const me = useMe()
  const canPII = canViewSalary(me)          // HR / Admin
  const admin = isAdminRole(me)
  // Manager / GM / Admin — see lib/role.js#canEditDentist for why Stock Manager
  // is out, and for the other two places that enforce the same set.
  const canEdit = canEditDentist(me)

  const id = Number(row?.id) || 0
  const [full, setFull] = useState(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [appt, setAppt] = useState({ rows: [], byId: new Map(), byDetail: new Map() })

  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState(null)
  const [saving, setSaving] = useState(false)
  const [saveErr, setSaveErr] = useState('')

  useEffect(() => {
    let alive = true
    setFull(null); setErr(''); setLoading(true); setEditing(false)
    if (!id) { setLoading(false); return }
    apiGet(`/api/doctors/${id}`)
      .then((d) => { if (alive) setFull(d) })
      .catch((e) => { if (alive) setErr(String(e.message || e)) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [id])

  // The spec/except catalog. `spec[]` holds appointmentList TYPE strings and
  // `except[]` holds appointmentList IDS, so one catalog drives both pickers.
  useEffect(() => {
    let alive = true
    cachedGet('/api/appointment-types')
      .then((r) => { if (alive) setAppt(indexAppointments(r)) })
      .catch(() => {})
    return () => { alive = false }
  }, [])

  const d = full || row || {}
  const a = activeInfo(d.active === true)

  function startEdit() {
    const src = full
    if (!src) return
    const opt = src.cash_receive_option || DEFAULT_CASH_OPTION
    setForm({
      prefix: src.prefix || '',
      name: src.name || '',
      surname: src.surname || '',
      nickname: src.nickname || '',
      name_eng: src.name_eng || '',
      phone: src.phone || '',
      email: src.email || '',
      license: src.license || '',
      idCard: src.idCard || '',
      address: src.address || '',
      spec: new Set(specList(src)),
      except: exceptIds(src, appt.byDetail),
      cash_receive_option: opt,
      bank_track: normBank(src.bank_track),
      bank_nontrack: normBank(src.bank_nontrack),
      credential: Array.isArray(src.credential) ? [...src.credential] : [],
    })
    setSaveErr('')
    setEditing(true)
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }))

  // Deselecting a spec drops the excepts that hung off it — otherwise the record
  // keeps opting out of treatments the doctor no longer claims, invisibly.
  function setSpec(next) {
    setForm((f) => ({ ...f, spec: next, except: pruneExcept(f.except, appt.byId, next) }))
  }

  async function save() {
    if (!form) return
    setSaving(true); setSaveErr('')
    const patch = {
      // `code` is derived, not typed — see lib/dentist.js#buildCode.
      code: buildCode({ nickname: form.nickname.trim(), name: form.name.trim() }),
      prefix: form.prefix.trim(),
      name: form.name.trim(),
      surname: form.surname.trim(),
      nickname: form.nickname.trim(),
      name_eng: form.name_eng.trim(),
      phone: form.phone.trim(),
      license: form.license.trim(),
      spec: [...form.spec],
      except: form.except,
      credential: form.credential.map((c) => String(c).trim()).filter(Boolean),
      ...bankPatch(form.cash_receive_option, form.bank_track, form.bank_nontrack),
    }
    if (admin) patch.email = form.email.trim()
    if (canPII) { patch.idCard = form.idCard.trim(); patch.address = form.address.trim() }
    try {
      const updated = await patchJson(`/api/doctors/${id}`, patch)
      applySaved(updated)
      setEditing(false)
    } catch (e) {
      setSaveErr(String(e.message || e))
    } finally {
      setSaving(false)
    }
  }

  // One place to land a server response: refresh the drawer, drop the memoized
  // /api/doctors lists (the booking calendar + revenue report read them), and let
  // the list page patch its row.
  function applySaved(updated) {
    const next = (updated && typeof updated === 'object' && updated.id) ? updated : null
    if (next) setFull(next)
    invalidateCache('/api/doctors')
    onSaved?.(next)
  }

  // Section-scoped writes (remarks, files, image) go straight to the server —
  // they are lists, not fields, so there is nothing to "cancel".
  async function patchField(body) {
    const updated = await patchJson(`/api/doctors/${id}`, body)
    applySaved(updated)
    return updated
  }

  const spec = editing ? [...form.spec] : specList(d)
  const except = editing
    ? form.except.map((x) => appt.byId.get(Number(x))?.detail || `#${x}`)
    : exceptList(d, appt.byId)
  const specOpts = useMemo(() => specChoices(appt.rows, d), [appt.rows, d])
  const exceptOpts = useMemo(
    () => exceptChoices(appt.rows, editing ? form?.spec : new Set(specList(d))),
    [appt.rows, editing, form?.spec, d],
  )

  const mirrored = isMirroredBank(editing ? form?.cash_receive_option : d.cash_receive_option)

  return (
    // Swallow drops that MISS the เอกสาร card: without this the browser
    // navigates away to the dropped file and the drawer (mid-edit) is gone.
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-end"
      role="dialog"
      aria-modal="true"
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => e.preventDefault()}
    >
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]" onMouseDown={editing ? undefined : onClose} />
      <div className="relative w-full max-w-[520px] bg-slate-50 shadow-2xl flex flex-col animate-ss-slide-in">

        {/* Header */}
        <div className="px-5 py-3.5 bg-white border-b border-slate-200 flex items-start gap-3">
          <ProfileImage doctor={d} canEdit={canEdit} onUploaded={applySaved} />
          <div className="min-w-0 flex-1">
            <div className="text-[15.5px] font-semibold text-slate-900 leading-tight truncate">{doctorCode(d)}</div>
            <div className="text-[12.5px] text-slate-500 truncate">{doctorFullName(d)}</div>
            <div className="text-[12px] text-slate-400 mt-0.5 flex items-center gap-1.5 flex-wrap">
              <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${a.tone}`}>{a.label}</span>
              <span>· หมอ #{d.id}</span>
            </div>
          </div>
          {/* Editing is gated on the FULL record: the slim list row carries no
              idCard/address/bank and resolves `except` to detail strings with no
              ids — seeding the form from it would save those fields back empty. */}
          {canEdit && !editing && (
            <button
              onClick={startEdit}
              disabled={!full}
              title={full ? '' : 'กำลังโหลดข้อมูล…'}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-[12.5px] font-medium text-slate-600 hover:border-brand-300 hover:text-brand-700 disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:text-slate-600"
            >
              {full ? <Pencil size={13} /> : <Loader2 size={13} className="animate-spin" />} แก้ไข
            </button>
          )}
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
          {err && <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] text-rose-600">โหลดข้อมูลไม่สำเร็จ · {err}</div>}

          {/* ── สถานะการใช้งาน — its own card, above everything, because this one
                flag decides whether the person can log in at all. Saves on the
                spot (it is a switch, not a form field). ── */}
          <ActiveCard doctor={full} canEdit={canEdit} loading={loading} onPatch={patchField} />

          {/* ── ข้อมูลทั่วไป (edit only — the header carries it in view mode) ── */}
          {editing && (
            <Section icon={Stethoscope} title="ข้อมูลทั่วไป">
              <Row2>
                <Field label="คำนำหน้า"><input className={INPUT} value={form.prefix} onChange={(e) => set('prefix', e.target.value)} /></Field>
                <Field label="ชื่อเล่น"><input className={INPUT} value={form.nickname} onChange={(e) => set('nickname', e.target.value)} /></Field>
              </Row2>
              <Row2>
                <Field label="ชื่อ"><input className={INPUT} value={form.name} onChange={(e) => set('name', e.target.value)} /></Field>
                <Field label="นามสกุล"><input className={INPUT} value={form.surname} onChange={(e) => set('surname', e.target.value)} /></Field>
              </Row2>
              <Field label="ชื่ออังกฤษ"><input className={INPUT} value={form.name_eng} onChange={(e) => set('name_eng', e.target.value)} /></Field>
              {/* Derived, never typed — "ชื่อเล่น ชื่อ". Shown so the effect of
                  editing those two fields is visible before saving. */}
              <Field label="ชื่อที่แสดง (code)" hint="สร้างจาก ชื่อเล่น + ชื่อ">
                <div className="rounded-lg bg-slate-50 px-3 py-1.5 text-[13.5px] text-slate-600">
                  {buildCode({ nickname: form.nickname, name: form.name }) || '—'}
                </div>
              </Field>
            </Section>
          )}

          {/* ── วิชาชีพ ── */}
          <Section icon={Stethoscope} title="วิชาชีพ">
            {editing ? (
              <>
                <Field label="เลขที่ใบประกอบ"><input className={INPUT} value={form.license} onChange={(e) => set('license', e.target.value)} /></Field>
                <Field label="ความเชี่ยวชาญ (spec)">
                  <SearchableSelect
                    multiple
                    options={specOpts}
                    values={form.spec}
                    onChange={setSpec}
                    placeholder="เลือกความเชี่ยวชาญ…"
                    searchPlaceholder="ค้นหาประเภทการรักษา…"
                  />
                  <TagRow
                    items={[...form.spec].map((s) => ({ key: s, label: specLabel(s) }))}
                    onRemove={(k) => setSpec(new Set([...form.spec].filter((s) => s !== k)))}
                    empty="ยังไม่ได้เลือก"
                  />
                </Field>
                <Field label="ไม่รับเคส (except)">
                  <SearchableSelect
                    multiple
                    options={exceptOpts}
                    values={new Set(form.except)}
                    onChange={(next) => set('except', [...next])}
                    disabled={!form.spec.size}
                    placeholder={form.spec.size ? 'เลือกเคสที่ไม่รับ…' : 'เลือกความเชี่ยวชาญก่อน'}
                    searchPlaceholder="ค้นหาเคส…"
                    emptyText="ไม่มีเคสในความเชี่ยวชาญที่เลือก"
                  />
                  <TagRow
                    tone="amber"
                    items={form.except.map((x) => ({ key: x, label: appt.byId.get(Number(x))?.detail || `#${x}` }))}
                    onRemove={(k) => set('except', form.except.filter((x) => x !== k))}
                    empty="รับทุกเคสในความเชี่ยวชาญ"
                  />
                </Field>
                <Field label="วุฒิการศึกษา / ใบรับรอง" hint="ลากเพื่อจัดลำดับ">
                  <ListEditor
                    items={form.credential}
                    onChange={(v) => set('credential', v)}
                    placeholder="เช่น ป.บัณฑิต ทันตกรรมทั่วไป"
                  />
                </Field>
              </>
            ) : (
              <>
                <Line k="เลขที่ใบประกอบ">{d.license || '—'}</Line>
                <div className="pt-1">
                  <div className="text-[12px] text-slate-400 mb-1">ความเชี่ยวชาญ</div>
                  {spec.length
                    ? <TagRow items={spec.map((s) => ({ key: s, label: specLabel(s) }))} />
                    : <span className="text-[13px] text-slate-400">ไม่ระบุ</span>}
                </div>
                {except.length > 0 && (
                  <div className="pt-1">
                    <div className="text-[12px] text-slate-400 mb-1 flex items-center gap-1"><Ban size={11} /> ไม่รับเคส</div>
                    <TagRow tone="amber" items={except.map((e, i) => ({ key: `${e}-${i}`, label: e }))} />
                  </div>
                )}
                {Array.isArray(full?.credential) && full.credential.filter(Boolean).length > 0 && (
                  <div className="pt-1">
                    <div className="text-[12px] text-slate-400 mb-1">วุฒิการศึกษา / ใบรับรอง</div>
                    <ul className="space-y-0.5">
                      {full.credential.filter(Boolean).map((c, i) => <li key={i} className="text-[13px] text-slate-700">· {c}</li>)}
                    </ul>
                  </div>
                )}
              </>
            )}
          </Section>

          {/* ── ติดต่อ ── */}
          <Section icon={Phone} title="ข้อมูลติดต่อ">
            {editing ? (
              <>
                <Field label="เบอร์โทร"><input className={INPUT} value={form.phone} onChange={(e) => set('phone', e.target.value)} /></Field>
                <Field label="อีเมล" hint={admin ? '' : 'ใช้เข้าสู่ระบบ doctor portal — แก้ไขได้เฉพาะ Admin'}>
                  <input className={INPUT} value={form.email} disabled={!admin} onChange={(e) => set('email', e.target.value)} />
                </Field>
                <Field label="ที่อยู่" hint={canPII ? '' : 'ดู/แก้ไขได้เฉพาะ HR / Admin'}>
                  <textarea rows={2} className={`${INPUT} resize-y`} value={canPII ? form.address : ''} disabled={!canPII} placeholder={canPII ? '' : '••••••'} onChange={(e) => set('address', e.target.value)} />
                </Field>
                <Field label="เลขบัตรประชาชน" hint={canPII ? '' : 'ดู/แก้ไขได้เฉพาะ HR / Admin'}>
                  <input className={INPUT} value={canPII ? form.idCard : maskTail(full?.idCard)} disabled={!canPII} onChange={(e) => set('idCard', e.target.value)} />
                </Field>
              </>
            ) : (
              <>
                <Line k="เบอร์โทร">{d.phone || '—'}</Line>
                <Line k="อีเมล"><span className="break-all">{d.email || '—'}</span></Line>
                {loading && !full ? <Loading /> : (
                  <>
                    <Line k="ที่อยู่">{full?.address ? (canPII ? full.address : <Hidden />) : '—'}</Line>
                    <Line k="เลขบัตรประชาชน">{full?.idCard ? (canPII ? full.idCard : maskTail(full.idCard)) : '—'}</Line>
                  </>
                )}
              </>
            )}
          </Section>

          {/* ── การรับเงิน ── */}
          <Section icon={Landmark} title="การรับเงิน">
            {loading && !full ? <Loading /> : editing ? (
              <>
                {/* บัญชีเดิม is the normal case and is not offered as a choice —
                    it is the value a doctor with no option set gets. The select
                    only appears once a doctor is genuinely split across two
                    accounts, which is also the way back. */}
                {!mirrored && (
                  <Field label="รูปแบบการรับเงิน">
                    <select className={INPUT} value={form.cash_receive_option} onChange={(e) => set('cash_receive_option', e.target.value)}>
                      {CASH_RECEIVE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Field>
                )}
                {mirrored ? (
                  <>
                    <BankFields bank={form.bank_track} onChange={(b) => set('bank_track', b)} />
                    <div className="text-[12px] text-slate-400 pt-0.5">ใช้บัญชีเดียวกันทั้งในระบบและนอกระบบ (บัญชีเดิม)</div>
                    <button
                      onClick={() => set('cash_receive_option', 'บัญชีใหม่')}
                      className="text-[12.5px] text-brand-700 hover:underline text-left"
                    >
                      แยกบัญชีในระบบ / นอกระบบ →
                    </button>
                  </>
                ) : (
                  <>
                    <div className="text-[12px] font-medium text-slate-500 pt-1">บัญชีในระบบ (bank_track)</div>
                    <BankFields bank={form.bank_track} onChange={(b) => set('bank_track', b)} />
                    <div className="text-[12px] font-medium text-slate-500 pt-2">บัญชีนอกระบบ (bank_nontrack)</div>
                    <BankFields bank={form.bank_nontrack} onChange={(b) => set('bank_nontrack', b)} />
                  </>
                )}
              </>
            ) : (
              <>
                <Line k="รูปแบบ">{full?.cash_receive_option || DEFAULT_CASH_OPTION}</Line>
                {mirrored ? (
                  <BankLine k="บัญชี" bank={full?.bank_track} />
                ) : (
                  <>
                    <BankLine k="บัญชีในระบบ" bank={full?.bank_track} />
                    <BankLine k="บัญชีนอกระบบ" bank={full?.bank_nontrack} />
                  </>
                )}
              </>
            )}
          </Section>

          {/* ── เอกสาร ── */}
          {!editing && <DentistFiles doctor={full} canEdit={canEdit} loading={loading} onPatch={patchField} />}

          {/* ── บันทึก ── */}
          {!editing && <DentistRemarks doctor={full} me={me} canEdit={canEdit} canDelete={canEdit} loading={loading} onPatch={patchField} />}
        </div>

        {/* Sticky save bar */}
        {editing && (
          <div className="border-t border-slate-200 bg-white px-5 py-3 space-y-2">
            {saveErr && <div className="text-[12.5px] text-rose-600">บันทึกไม่สำเร็จ · {saveErr}</div>}
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setEditing(false)} disabled={saving} className="rounded-lg border border-slate-200 px-3.5 py-2 text-[13px] text-slate-600 hover:bg-slate-50">ยกเลิก</button>
              <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-4 py-2 text-[13px] font-semibold text-white hover:bg-brand-700 disabled:opacity-40">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} บันทึก
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/* ── สถานะการใช้งาน ─────────────────────────────────────────────────────────
 * `active` is not just a list filter: an inactive doctor is refused at LOGIN by
 * both the CRM and doctor.smileseasons.com. That makes it the single most
 * consequential switch on this screen, so it gets its own card at the top and
 * spells out the consequence rather than sitting as a checkbox among names.  */
function ActiveCard({ doctor, canEdit, loading, onPatch }) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [asking, setAsking] = useState(false)
  const [resending, setResending] = useState(false)
  const [askingResend, setAskingResend] = useState(false)
  const [resendNote, setResendNote] = useState('')
  const on = doctor?.active === true

  // Turning OFF locks the doctor out of two systems, so it asks first; turning
  // back on is harmless and applies straight away.
  function toggle() {
    if (on) setAsking(true)
    else apply(true)
  }

  async function apply(next) {
    setAsking(false)
    setBusy(true); setErr('')
    try { await onPatch({ active: next }) }
    catch (e) { setErr(String(e.message || e)) }
    finally { setBusy(false) }
  }

  // The invite token dies in five minutes, so the mail sent at create time is
  // usually already dead. This re-sends it to the address ON THE RECORD — the
  // endpoint reads it there and ignores anything we could send, so what is shown
  // in the confirm below is what will actually be mailed.
  async function resend() {
    setAskingResend(false)
    setResending(true); setErr(''); setResendNote('')
    try {
      const r = await postJson('/api/doctors/resend-invite', { id: doctor.id })
      setResendNote(`ส่งอีเมลยืนยันไปที่ ${r?.email || doctor.email} แล้ว — ลิงก์หมดอายุใน 5 นาที`)
    } catch (e) {
      // The proxy answers a refusal with a Thai {error}; postJson wraps it as
      // "HTTP 4xx: <json>" and hands the parsed body over on err.body.
      setErr(String(e.body?.error || e.message || e))
    } finally { setResending(false) }
  }

  return (
    <section className={`rounded-xl border p-4 ${on ? 'border-emerald-200 bg-emerald-50/50' : 'border-amber-300 bg-amber-50'}`}>
      <div className="flex items-center gap-3">
        <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${on ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
          {on ? <Power size={17} /> : <AlertTriangle size={17} />}
        </span>
        <div className="min-w-0 flex-1">
          <div className={`text-[14.5px] font-semibold ${on ? 'text-emerald-800' : 'text-amber-800'}`}>
            {on ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
          </div>
          <p className={`text-[12px] leading-relaxed ${on ? 'text-emerald-700/80' : 'text-amber-700'}`}>
            {on
              ? 'เข้าสู่ระบบ CRM และ doctor.smileseasons.com ได้ตามปกติ'
              : 'เข้าสู่ระบบ CRM และ doctor.smileseasons.com ไม่ได้'}
          </p>
        </div>
        {canEdit && !loading && (
          <button
            onClick={toggle}
            disabled={busy || !doctor}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12.5px] font-semibold shadow-sm disabled:opacity-40 ${
              on ? 'bg-white text-amber-700 ring-1 ring-amber-300 hover:bg-amber-50' : 'bg-emerald-600 text-white hover:bg-emerald-700'
            }`}
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Power size={13} />}
            {on ? 'ปิดใช้งาน' : 'เปิดใช้งาน'}
          </button>
        )}
      </div>
      {/* ── ส่งอีเมลเชิญอีกครั้ง ──
          Only while the doctor is OFF, which for a doctor is the "invited, has not
          answered" state: /doctor/new writes both rows dormant and only the claim
          link switches them on. Once they are on, the mail's own promise ("บัญชีจะยัง
          ใช้งานไม่ได้") is false and the endpoint refuses it — ลืมรหัสผ่าน on the portal
          is their route from then on. */}
      {!on && canEdit && !loading && (
        <div className="mt-3 pt-3 border-t border-amber-200/70">
          <div className="flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-[12.5px] font-semibold text-amber-800">ยังไม่ได้ยืนยันบัญชี?</div>
              <p className="text-[11.5px] text-amber-700/90 leading-relaxed">
                ส่งอีเมล “ยืนยันและตั้งรหัสผ่าน” ไปที่ {doctor?.email || '— ยังไม่มีอีเมล —'} อีกครั้ง
                {' '}— ลิงก์เดิมหมดอายุใน 5 นาที
              </p>
            </div>
            <button
              onClick={() => setAskingResend(true)}
              disabled={resending || !doctor?.id || !doctor?.email}
              className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12.5px] font-semibold shadow-sm bg-white text-amber-800 ring-1 ring-amber-300 hover:bg-amber-50 disabled:opacity-40 shrink-0"
            >
              {resending ? <Loader2 size={13} className="animate-spin" /> : <MailCheck size={13} />}
              ส่งอีเมลอีกครั้ง
            </button>
          </div>
          {resendNote && <div className="text-[12px] text-emerald-700 mt-2">{resendNote}</div>}
        </div>
      )}
      {err && <div className="text-[12px] text-rose-600 mt-2">{err}</div>}
      {asking && (
        <ConfirmDialog
          title="ปิดใช้งานหมอท่านนี้?"
          message="จะเข้าสู่ระบบ CRM และ doctor.smileseasons.com ไม่ได้ทันที — เปิดกลับได้ภายหลัง"
          confirmLabel="ปิดใช้งาน"
          tone="danger"
          onConfirm={() => apply(false)}
          onCancel={() => setAsking(false)}
        />
      )}
      {askingResend && (
        <ConfirmDialog
          title="ส่งอีเมลยืนยันอีกครั้ง?"
          message={`ระบบจะส่งลิงก์ “ยืนยันและตั้งรหัสผ่าน” ไปที่ ${doctor?.email || ''} — ลิงก์ใช้ได้ครั้งเดียวและหมดอายุใน 5 นาที`}
          confirmLabel="ส่งอีเมล"
          onConfirm={resend}
          onCancel={() => setAskingResend(false)}
        />
      )}
    </section>
  )
}

/* ── profile image ───────────────────────────────────────────────────────────
 * Hosted by XANO (not Wasabi): its `POST /doctor/profile_image` deletes the
 * previous object from Xano storage, stores the new one publicly and writes the
 * URL back to the record — so this is an upload, not a field edit.            */

/**
 * Shrink a picked photo before it goes anywhere.
 *
 * 🔑 THIS IS HALF OF A 1102 FIX. A phone camera JPEG is 3–8 MB and this avatar
 * renders at 44 px. Because Xano owns the stored file there is no Wasabi URL to
 * presign (functions/api/doctors/profile-image.js says why), so every one of those
 * megabytes has to cross the Pages Worker that all our /api routes share — the
 * isolate whose 128 MB ceiling is what served Error 1102 to unrelated page loads
 * on 2026-08-27/28. Capping the long side at 512 px turns 8 MB into ~60 KB. The
 * route streams rather than buffers; this is what makes the stream tiny.
 *
 * NOT `generateThumbnail()` from src/lib/thumbnail.js: that one makes a 240 px WebP
 * to store inline on a record. This IS the stored image, so it stays JPEG — Xano's
 * storage.create_image is what receives it, and a format change there is not
 * something to discover in production.
 *
 * Best effort in every direction: an image that does not need resizing is left
 * exactly as it is (a small PNG keeps its transparency), a re-encode that came out
 * bigger is thrown away, and anything that fails — a HEIC `createImageBitmap`
 * cannot decode, a canvas that will not export — returns the original file. A
 * shrink is an optimisation; it may never be the reason an upload fails.
 */
async function shrinkForAvatar(file, { maxSize = 512, quality = 0.85, skipUnder = 512 * 1024 } = {}) {
  if (!file || !/^image\//.test(file.type || '')) return file
  let bitmap
  try { bitmap = await createImageBitmap(file) } catch { return file }
  try {
    const longest = Math.max(bitmap.width, bitmap.height)
    if (longest <= maxSize && (file.size || 0) <= skipUnder) return file
    const scale = Math.min(1, maxSize / longest)
    const w = Math.max(1, Math.round(bitmap.width * scale))
    const h = Math.max(1, Math.round(bitmap.height * scale))
    let blob
    if (typeof OffscreenCanvas !== 'undefined') {
      const c = new OffscreenCanvas(w, h)
      c.getContext('2d').drawImage(bitmap, 0, 0, w, h)
      blob = await c.convertToBlob({ type: 'image/jpeg', quality })
    } else {
      const c = document.createElement('canvas')
      c.width = w; c.height = h
      c.getContext('2d').drawImage(bitmap, 0, 0, w, h)
      blob = await new Promise((r) => c.toBlob(r, 'image/jpeg', quality))
    }
    if (!blob || blob.size >= (file.size || Infinity)) return file
    const base = String(file.name || 'profile').replace(/\.[^.]+$/, '') || 'profile'
    return new File([blob], `${base}.jpg`, { type: 'image/jpeg' })
  } catch {
    return file
  } finally {
    bitmap.close?.()
  }
}

// Remembered for the tab's lifetime once a streamed upload has been shown not to
// work here, so we pay that failed round trip once rather than once per photo.
// Same idea, and the same reason, as `directBroken` in src/lib/uploadDirect.js; a
// reload re-tests it, which is what you want after the upstream is fixed.
let streamBroken = false

function ProfileImage({ doctor, canEdit, onUploaded }) {
  const [busy, setBusy] = useState(false)
  const [broken, setBroken] = useState(false)
  const [err, setErr] = useState('')
  const inputRef = useRef(null)
  const src = doctor?.profile_img

  useEffect(() => { setBroken(false) }, [src])

  /**
   * Two attempts, in this order — the same shape, for the same reason, as
   * src/lib/uploadDirect.js:
   *
   *   1. `?stream=1` — the Worker PIPES the body to Xano and buffers none of it,
   *      which is what keeps a profile photo out of the shared isolate's memory.
   *      `doctor_id` rides in the query as well as in the form because a streamed
   *      body is the one thing the Worker cannot read.
   *   2. the plain POST — the Worker's original buffered proxy. A failed stream is
   *      retried the slow way rather than shown to the user: the file is still in
   *      the browser, so the retry costs one more upload of an image we have
   *      already shrunk to ~60 KB, and the upload still succeeds.
   *
   * 401/403 are OUR answer and not a transport problem — retrying them would only
   * produce the same refusal, so they are reported as-is. And `streamBroken` is set
   * only when the retry SUCCEEDS: "the stream failed and the buffer worked" is the
   * one signal that actually means the streamed path is unusable here.
   */
  async function pick(e) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || !doctor?.id) return
    setBusy(true); setErr('')
    try {
      const send = await shrinkForAvatar(file)
      const post = (qs) => {
        const fd = new FormData()
        fd.append('file', send, send.name || 'profile.jpg')
        fd.append('doctor_id', String(doctor.id))   // the copy Xano reads
        return fetch(`/api/doctors/profile-image${qs}`, { method: 'POST', body: fd, credentials: 'include' })
      }

      let res = null
      if (!streamBroken) {
        res = await post(`?stream=1&doctor_id=${doctor.id}`).catch(() => null)
        if (res && !res.ok && res.status !== 401 && res.status !== 403) res = null
      }
      if (!res) {
        const buffered = await post('')
        if (buffered.ok && !streamBroken) {
          streamBroken = true
          console.warn('[profile-image] streamed upload unavailable — falling back to the buffered proxy')
        }
        res = buffered
      }

      if (!res.ok) throw new Error(await res.text().catch(() => `HTTP ${res.status}`))
      onUploaded?.(await res.json())
    } catch (e2) {
      setErr(String(e2.message || e2))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="shrink-0">
      <button
        type="button"
        onClick={canEdit ? () => inputRef.current?.click() : undefined}
        disabled={!canEdit || busy}
        title={canEdit ? 'เปลี่ยนรูปโปรไฟล์' : ''}
        className="relative w-11 h-11 rounded-full overflow-hidden group disabled:cursor-default"
      >
        {src && !broken
          ? <img src={src} alt="" onError={() => setBroken(true)} className="w-11 h-11 rounded-full object-cover ring-1 ring-slate-200" />
          : <span className="w-11 h-11 rounded-full bg-brand-50 text-brand-600 text-[16px] font-semibold flex items-center justify-center">
              {doctor?.code || doctor?.name ? nameInitial(doctorCode(doctor)) : <Stethoscope size={18} />}
            </span>}
        {canEdit && (
          <span className="absolute inset-0 rounded-full bg-slate-900/50 text-white items-center justify-center hidden group-hover:flex">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
          </span>
        )}
        {busy && <span className="absolute inset-0 rounded-full bg-slate-900/50 text-white flex items-center justify-center"><Loader2 size={14} className="animate-spin" /></span>}
      </button>
      <input ref={inputRef} type="file" accept="image/*" hidden onChange={pick} />
      {err && <div className="text-[11px] text-rose-600 mt-1 max-w-[120px]">{err}</div>}
    </div>
  )
}

/* ── files ─────────────────────────────────────────────────────────────────── */
function DentistFiles({ doctor, canEdit, loading, onPatch }) {
  const files = Array.isArray(doctor?.file) ? doctor.file : []
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [editKey, setEditKey] = useState(null)
  const [name, setName] = useState('')
  const [tag, setTag] = useState('')
  const [dragging, setDragging] = useState(false)
  const [progress, setProgress] = useState(null) // { done, total }
  const [pending, setPending] = useState(null)   // file queued for deletion
  const inputRef = useRef(null)
  // dragenter/dragleave fire for every child element the pointer crosses, so a
  // boolean flickers. Count enters vs leaves and only clear at zero.
  const dragDepth = useRef(0)

  const keyOf = (f, i) => f?.uuid || f?.location || `i${i}`

  /**
   * Upload one or many files, then write the record ONCE.
   *
   * If a file fails partway, the ones already in Wasabi are still PATCHed on —
   * throwing instead would leave them uploaded but unreferenced, which is
   * invisible to the user and orphans the objects.
   */
  async function onFiles(list) {
    const picked = [...(list || [])]
    if (!picked.length || !doctor?.id) return
    setBusy(true); setErr(''); setProgress({ done: 0, total: picked.length })
    const added = []
    let failure = ''
    try {
      for (const file of picked) {
        try {
          // Upload and shrink in parallel — the thumbnail never gates the upload.
          // The upload goes straight to Wasabi where CORS allows and falls back to
          // the old proxy where it does not — src/lib/uploadDirect.js.
          const [uploaded, thumbBlob] = await Promise.all([
            uploadFile('/api/dentist-upload', { code: doctor.code || `doctor-${doctor.id}` }, file),
            generateThumbnail(file).catch(() => null),
          ])
          const { ok, ...entry } = uploaded
          // Thumbnail stored INLINE as a data-URI (the same call the Expense
          // attachments make): a doctor carries a handful of files, so a second
          // Wasabi object per image would cost an extra upload, sign and cleanup
          // for no gain. Non-images resolve to '' and fall back to an icon.
          const thumbnail = await blobToDataURL(thumbBlob)
          added.push({ ...entry, thumbnail, tag: [], note: '' })
        } catch (e2) {
          failure = `${file.name}: ${String(e2.message || e2)}`
          break
        } finally {
          setProgress((p) => (p ? { ...p, done: p.done + 1 } : p))
        }
      }
      if (added.length) await onPatch({ file: [...files, ...added] })
      if (failure) setErr(`อัปโหลดไม่สำเร็จ — ${failure}${added.length ? ` (บันทึกแล้ว ${added.length} ไฟล์)` : ''}`)
    } catch (e2) {
      setErr(String(e2.message || e2))
    } finally {
      setBusy(false); setProgress(null)
    }
  }

  function onDrop(e) {
    e.preventDefault()
    dragDepth.current = 0
    setDragging(false)
    if (!canEdit || busy) return
    onFiles(e.dataTransfer?.files)
  }

  async function remove(f, i) {
    setPending(null)
    setBusy(true); setErr('')
    try {
      await onPatch({ file: files.filter((_, j) => j !== i) })
      // Best-effort object cleanup — the record is already clean either way.
      if (f?.location) {
        fetch(`/api/dentist-file/${f.location}`, { method: 'DELETE', credentials: 'include' }).catch(() => {})
      }
    } catch (e2) {
      setErr(String(e2.message || e2))
    } finally {
      setBusy(false)
    }
  }

  function startRename(f, i) {
    setEditKey(keyOf(f, i))
    setName(f?.name || '')
    setTag(Array.isArray(f?.tag) ? (f.tag[0] || '') : (f?.tag || ''))
  }

  async function commitRename(i) {
    setBusy(true); setErr('')
    try {
      const next = files.map((f, j) => (j === i ? { ...f, name: name.trim() || f.name, tag: tag ? [tag] : [], modified_at: Date.now() } : f))
      await onPatch({ file: next })
      setEditKey(null)
    } catch (e2) {
      setErr(String(e2.message || e2))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      onDragEnter={(e) => { if (!canEdit) return; e.preventDefault(); dragDepth.current += 1; setDragging(true) }}
      onDragOver={(e) => { if (!canEdit) return; e.preventDefault(); e.dataTransfer.dropEffect = 'copy' }}
      onDragLeave={(e) => { if (!canEdit) return; e.preventDefault(); dragDepth.current -= 1; if (dragDepth.current <= 0) { dragDepth.current = 0; setDragging(false) } }}
      onDrop={onDrop}
    >
    <Section
      icon={FileText}
      title={`เอกสาร${files.length ? ` (${files.length})` : ''}`}
      right={canEdit && !loading && (
        <button onClick={() => inputRef.current?.click()} disabled={busy} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 text-[12px] text-slate-600 hover:border-brand-300 hover:text-brand-700 disabled:opacity-40">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} อัปโหลด
        </button>
      )}
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        hidden
        onChange={(e) => { const l = e.target.files; e.target.value = ''; onFiles(l) }}
      />
      {loading && <Loading />}

      {/* Drop target — the whole card accepts a drop (see the wrapper below), but
          this gives the gesture somewhere obvious to aim at. */}
      {canEdit && !loading && (
        <div
          onClick={() => !busy && inputRef.current?.click()}
          className={`rounded-xl border border-dashed py-4 text-center transition cursor-pointer ${
            dragging ? 'border-brand-400 bg-brand-50' : 'border-slate-200 hover:border-brand-300 hover:bg-brand-50/30'
          } ${busy ? 'pointer-events-none opacity-70' : ''}`}
        >
          {busy ? (
            <div className="text-[12.5px] text-slate-500">
              <Loader2 size={16} className="inline animate-spin mr-1.5" />
              กำลังอัปโหลด{progress ? ` ${progress.done}/${progress.total}` : ''}…
            </div>
          ) : (
            <div className="text-slate-500">
              <Upload size={18} className={`mx-auto mb-1 ${dragging ? 'text-brand-500' : 'text-slate-400'}`} />
              <div className="text-[12.5px] font-medium">{dragging ? 'วางไฟล์เพื่ออัปโหลด' : 'คลิกหรือลากไฟล์มาวางที่นี่'}</div>
              <div className="text-[11.5px] text-slate-400 mt-0.5">ลากพร้อมกันได้หลายไฟล์</div>
            </div>
          )}
        </div>
      )}

      {err && <div className="text-[12px] text-rose-600">{err}</div>}
      {!loading && !files.length && !canEdit && <div className="text-[13px] text-slate-400">ยังไม่มีเอกสาร</div>}
      <ul className="space-y-1.5">
        {files.map((f, i) => {
          const href = dentistFileHref(f)
          const k = keyOf(f, i)
          if (editKey === k) {
            return (
              <li key={k} className="rounded-lg bg-slate-50 p-2 space-y-1.5">
                <input autoFocus value={name} onChange={(e) => setName(e.target.value)} className={INPUT} placeholder="ชื่อไฟล์" />
                <select value={tag} onChange={(e) => setTag(e.target.value)} className={INPUT}>
                  <option value="">— ไม่ระบุประเภท —</option>
                  {FILE_TAGS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <div className="flex items-center justify-end gap-2">
                  <button onClick={() => setEditKey(null)} className="rounded-lg border border-slate-200 px-2.5 py-1 text-[12px] text-slate-600 hover:bg-white">ยกเลิก</button>
                  <button onClick={() => commitRename(i)} disabled={busy} className="rounded-lg bg-brand-600 px-3 py-1 text-[12px] font-semibold text-white hover:bg-brand-700 disabled:opacity-40">บันทึก</button>
                </div>
              </li>
            )
          }
          return (
            <li key={k} className="flex items-center gap-2 min-w-0 group">
              <FileThumb file={f} href={href} />
              <div className="min-w-0 flex-1">
                {href
                  ? <a href={href} target="_blank" rel="noopener noreferrer" className="block text-[13px] text-brand-700 hover:underline truncate">{f?.name || `ไฟล์ ${i + 1}`}</a>
                  : <span className="block text-[13px] text-slate-600 truncate">{f?.name || `ไฟล์ ${i + 1}`}</span>}
                <div className="flex items-center gap-1.5 mt-0.5">
                  {fileTagText(f) && <span className="inline-flex items-center rounded-md bg-slate-100 text-slate-500 px-1.5 py-0.5 text-[11px]">{fileTagText(f)}</span>}
                  {f?.size ? <span className="text-[11px] text-slate-400">{fmtBytes(f.size)}</span> : null}
                </div>
              </div>
              {canEdit && (
                <span className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition shrink-0">
                  <button onClick={() => startRename(f, i)} title="แก้ไข" className="p-1 text-slate-400 hover:text-brand-600"><Pencil size={12} /></button>
                  <button onClick={() => setPending({ f, i })} title="ลบ" className="p-1 text-slate-400 hover:text-rose-600"><Trash2 size={12} /></button>
                </span>
              )}
            </li>
          )
        })}
      </ul>
    </Section>
    {pending && (
      <ConfirmDialog
        title="ลบเอกสารนี้?"
        message={`"${pending.f?.name || 'ไฟล์นี้'}" จะถูกลบออกจากประวัติหมอและลบไฟล์ออกจากคลังถาวร`}
        confirmLabel="ลบ"
        tone="danger"
        onConfirm={() => remove(pending.f, pending.i)}
        onCancel={() => setPending(null)}
      />
    )}
    </div>
  )
}

/**
 * File row preview. Three tiers, in order:
 *  1. the stored inline `thumbnail` (data-URI, written at upload) — free to show;
 *  2. for an image WITHOUT one — files uploaded before thumbnails existed — the
 *     proxy URL, `loading="lazy"` so it only costs a fetch when scrolled into
 *     view. This pulls the FULL-size object, hence not the first choice;
 *  3. a document icon for everything else, and for images that fail to load
 *     (the legacy test row points at a key that does not exist).
 */
function FileThumb({ file, href }) {
  const [failed, setFailed] = useState(false)
  const src = file?.thumbnail || (isImageFile(file) && !failed ? href : '')
  const box = 'w-9 h-9 rounded-md shrink-0 overflow-hidden'

  if (!src || failed) {
    return <span className={`${box} bg-slate-100 text-slate-300 flex items-center justify-center`}><FileText size={14} /></span>
  }
  const img = (
    <img
      src={src}
      alt=""
      loading="lazy"
      onError={() => setFailed(true)}
      className="w-full h-full object-cover"
    />
  )
  return href
    ? <a href={href} target="_blank" rel="noopener noreferrer" className={`${box} ring-1 ring-slate-200 hover:ring-brand-300 block`}>{img}</a>
    : <span className={`${box} ring-1 ring-slate-200`}>{img}</span>
}

/* ── remarks ───────────────────────────────────────────────────────────────── */
function DentistRemarks({ doctor, me, canEdit, canDelete, loading, onPatch }) {
  const all = Array.isArray(doctor?.remark) ? doctor.remark : []
  // Rows with no message are migration noise (every legacy doctor carries one).
  const shown = all.map((r, i) => ({ r, i })).filter(({ r }) => r && r.message)
  const staff = useEmployeeCodes(shown.map(({ r }) => r.employee_id))
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState('')
  const [editIdx, setEditIdx] = useState(null)
  const [pending, setPending] = useState(null) // remark index queued for deletion

  async function commit(next) {
    setBusy(true); setErr('')
    try { await onPatch({ remark: next }); return true }
    catch (e) { setErr(String(e.message || e)); return false }
    finally { setBusy(false) }
  }

  async function add() {
    const msg = draft.trim()
    if (!msg) { setAdding(false); return }
    const entry = {
      message: msg,
      employee_id: Number(me?.employee_id) || 0,
      timestamp: Date.now(),
      uuid: (crypto?.randomUUID?.() || `r${Date.now()}`),
    }
    if (await commit([...all, entry])) { setDraft(''); setAdding(false) }
  }

  async function saveEdit(idx) {
    const msg = draft.trim()
    if (!msg) return
    if (await commit(all.map((r, j) => (j === idx ? { ...r, message: msg } : r)))) { setEditIdx(null); setDraft('') }
  }

  async function remove(idx) {
    setPending(null)
    await commit(all.filter((_, j) => j !== idx))
  }

  return (
    <Section
      icon={MessageSquare}
      title={`บันทึก${shown.length ? ` (${shown.length})` : ''}`}
      right={canEdit && !loading && !adding && (
        <button onClick={() => { setAdding(true); setDraft(''); setEditIdx(null) }} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 text-[12px] text-slate-600 hover:border-brand-300 hover:text-brand-700">
          <Plus size={12} /> เพิ่ม
        </button>
      )}
    >
      {loading && <Loading />}
      {err && <div className="text-[12px] text-rose-600">{err}</div>}
      {adding && (
        <div className="space-y-1.5">
          <textarea autoFocus rows={2} value={draft} onChange={(e) => setDraft(e.target.value)} className={`${INPUT} resize-y`} placeholder="เขียนบันทึก…" />
          <div className="flex items-center justify-end gap-2">
            <button onClick={() => setAdding(false)} className="rounded-lg border border-slate-200 px-2.5 py-1 text-[12px] text-slate-600 hover:bg-slate-50">ยกเลิก</button>
            <button onClick={add} disabled={busy || !draft.trim()} className="rounded-lg bg-brand-600 px-3 py-1 text-[12px] font-semibold text-white hover:bg-brand-700 disabled:opacity-40">บันทึก</button>
          </div>
        </div>
      )}
      {!loading && !shown.length && !adding && <div className="text-[13px] text-slate-400">ยังไม่มีบันทึก</div>}
      <ul className="space-y-2">
        {shown.map(({ r, i }) => (
          <li key={r.uuid || i} className="rounded-lg bg-slate-50 px-3 py-2 group">
            {editIdx === i ? (
              <div className="space-y-1.5">
                <textarea autoFocus rows={2} value={draft} onChange={(e) => setDraft(e.target.value)} className={`${INPUT} resize-y`} />
                <div className="flex items-center justify-end gap-2">
                  <button onClick={() => setEditIdx(null)} className="rounded-lg border border-slate-200 px-2.5 py-1 text-[12px] text-slate-600 hover:bg-white">ยกเลิก</button>
                  <button onClick={() => saveEdit(i)} disabled={busy} className="rounded-lg bg-brand-600 px-3 py-1 text-[12px] font-semibold text-white hover:bg-brand-700 disabled:opacity-40">บันทึก</button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-start gap-2">
                  <div className="text-[13px] text-slate-700 whitespace-pre-wrap flex-1 min-w-0">{r.message}</div>
                  {canEdit && (
                    <span className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition shrink-0">
                      <button onClick={() => { setEditIdx(i); setDraft(r.message); setAdding(false) }} title="แก้ไข" className="p-1 text-slate-400 hover:text-brand-600"><Pencil size={12} /></button>
                      {canDelete && <button onClick={() => setPending({ idx: i, text: r.message })} title="ลบ" className="p-1 text-slate-400 hover:text-rose-600"><Trash2 size={12} /></button>}
                    </span>
                  )}
                </div>
                <div className="text-[11.5px] text-slate-400 mt-0.5">
                  {r.employee?.code || staff.get(Number(r.employee_id)) || (r.employee_id ? `พนักงาน #${r.employee_id}` : '—')}
                  {r.timestamp ? ` · ${fmtDateTH(r.timestamp)}` : ''}
                </div>
              </>
            )}
          </li>
        ))}
      </ul>
      {pending && (
        <ConfirmDialog
          title="ลบบันทึกนี้?"
          message={pending.text ? `"${String(pending.text).slice(0, 80)}${String(pending.text).length > 80 ? '…' : ''}"` : 'การลบไม่สามารถย้อนกลับได้'}
          confirmLabel="ลบ"
          tone="danger"
          onConfirm={() => remove(pending.idx)}
          onCancel={() => setPending(null)}
        />
      )}
    </Section>
  )
}

/**
 * id → employee `code` for remark bylines. `GET /doctor/{id}` returns the raw
 * `employee_id` (only the LIST endpoint resolves it through an addon), and a
 * bare "#1" tells a manager nothing. One cached fetch per distinct id, shared
 * app-wide by cachedGet.
 */
function useEmployeeCodes(ids) {
  const [map, setMap] = useState(new Map())
  const key = [...new Set((ids || []).map(Number).filter(Boolean))].sort().join(',')
  useEffect(() => {
    if (!key) return
    let alive = true
    const wanted = key.split(',').map(Number)
    Promise.all(wanted.map((id) =>
      cachedGet(`/api/employees/${id}`)
        .then((e) => [id, e?.code || [e?.prefix, e?.name].filter(Boolean).join('') || ''])
        .catch(() => [id, ''])))
      .then((pairs) => {
        if (!alive) return
        setMap(new Map(pairs.filter(([, v]) => v)))
      })
    return () => { alive = false }
  }, [key])
  return map
}

/* ── small pieces ──────────────────────────────────────────────────────────── */
function Section({ icon: Icon, title, right, children }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 space-y-1.5">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="text-[12px] font-medium uppercase tracking-wide text-slate-400 flex items-center gap-1.5">
          <Icon size={12} /> {title}
        </div>
        {right}
      </div>
      {children}
    </section>
  )
}

function Field({ label, hint, children }) {
  return (
    <div className="space-y-1">
      <div className="text-[12px] text-slate-400">{label}{hint && <span className="ml-1 text-slate-300">· {hint}</span>}</div>
      {children}
    </div>
  )
}
const Row2 = ({ children }) => <div className="grid grid-cols-2 gap-2">{children}</div>

function Line({ k, children }) {
  return (
    <div className="flex gap-2 text-[13px]">
      <span className="text-slate-400 shrink-0 w-[112px]">{k}</span>
      <span className="min-w-0 text-slate-700">{children}</span>
    </div>
  )
}

function BankLine({ k, bank }) {
  const parts = [bank?.bank_name, bank?.bank_num].filter(Boolean)
  if (!parts.length) return <Line k={k}>—</Line>
  return (
    <Line k={k}>
      {parts.join(' · ')}
      {bank?.bank_branchcode && <span className="text-slate-400"> · สาขา {bank.bank_branchcode}</span>}
    </Line>
  )
}

function BankFields({ bank, onChange }) {
  const b = bank || emptyBank()
  const set = (k, v) => onChange({ ...b, [k]: v })
  // The stored value wins even when it is not in the catalog: the column is free
  // text, so a value from outside the list can always turn up. Without this the
  // picker would show blank and silently rewrite the bank on the next save.
  const names = b.bank_name && !BANKS.includes(b.bank_name) ? [b.bank_name, ...BANKS] : BANKS
  const options = [{ value: '', label: '— ไม่ระบุธนาคาร —' }, ...names.map((x) => ({ value: x, label: x }))]
  return (
    <div className="space-y-1.5">
      <SearchableSelect
        options={options}
        value={b.bank_name}
        onChange={(v) => set('bank_name', v)}
        placeholder="เลือกธนาคาร"
        searchPlaceholder="พิมพ์ชื่อธนาคาร…"
        emptyText="ไม่พบธนาคาร"
        buttonClassName="!rounded-lg !py-1.5"
      />
      <Row2>
        <input className={INPUT} value={b.bank_num} onChange={(e) => set('bank_num', e.target.value)} placeholder="เลขที่บัญชี" />
        <input className={INPUT} value={b.bank_branchcode} onChange={(e) => set('bank_branchcode', e.target.value)} placeholder="รหัสสาขา" />
      </Row2>
    </div>
  )
}

function TagRow({ items, onRemove, tone = 'brand', empty }) {
  if (!items?.length) return empty ? <div className="text-[12.5px] text-slate-400">{empty}</div> : null
  const cls = tone === 'amber' ? 'bg-amber-50 text-amber-700' : 'bg-brand-50 text-brand-700'
  return (
    <div className="flex flex-wrap gap-1 pt-1">
      {items.map((t) => (
        <span key={t.key} className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[12px] ${cls}`}>
          {t.label}
          {onRemove && <button onClick={() => onRemove(t.key)} className="opacity-60 hover:opacity-100"><X size={11} /></button>}
        </span>
      ))}
    </div>
  )
}

/**
 * Free-text ordered list (credential[]) — add / edit in place / remove / REORDER.
 *
 * Order is content here: the doctor portal shows this CV top-down (degree →
 * specialty → certificates), so the array index IS the display rank. Reordering
 * uses native HTML5 drag-and-drop — the doctor portal reaches for framer-motion's
 * Reorder, but this app carries no animation library and a drag handle plus ↑/↓
 * buttons covers the same ground (and the buttons work on touch, where HTML5 drag
 * does not).
 */
function ListEditor({ items, onChange, placeholder }) {
  const [draft, setDraft] = useState('')
  const [dragIdx, setDragIdx] = useState(null)
  const [overIdx, setOverIdx] = useState(null)

  function move(from, to) {
    if (to < 0 || to >= items.length || from === to) return
    const next = [...items]
    const [row] = next.splice(from, 1)
    next.splice(to, 0, row)
    onChange(next)
  }

  return (
    <div className="space-y-1.5">
      {items.map((c, i) => (
        <div
          key={i}
          onDragOver={(e) => { e.preventDefault(); setOverIdx(i) }}
          onDrop={(e) => { e.preventDefault(); if (dragIdx != null) move(dragIdx, i); setDragIdx(null); setOverIdx(null) }}
          className={`flex items-center gap-1 rounded-lg transition ${
            overIdx === i && dragIdx != null && dragIdx !== i ? 'ring-2 ring-brand-300' : ''
          } ${dragIdx === i ? 'opacity-40' : ''}`}
        >
          <span
            draggable
            onDragStart={(e) => { setDragIdx(i); e.dataTransfer.effectAllowed = 'move' }}
            onDragEnd={() => { setDragIdx(null); setOverIdx(null) }}
            title="ลากเพื่อจัดลำดับ"
            className="cursor-grab active:cursor-grabbing p-1 text-slate-300 hover:text-slate-500 shrink-0"
          >
            <GripVertical size={14} />
          </span>
          <input className={INPUT} value={c} onChange={(e) => onChange(items.map((x, j) => (j === i ? e.target.value : x)))} />
          <div className="flex flex-col shrink-0">
            <button onClick={() => move(i, i - 1)} disabled={i === 0} title="เลื่อนขึ้น" className="text-slate-300 hover:text-brand-600 disabled:opacity-30 leading-none"><ChevronUp size={13} /></button>
            <button onClick={() => move(i, i + 1)} disabled={i === items.length - 1} title="เลื่อนลง" className="text-slate-300 hover:text-brand-600 disabled:opacity-30 leading-none"><ChevronDown size={13} /></button>
          </div>
          <button onClick={() => onChange(items.filter((_, j) => j !== i))} className="p-1.5 text-slate-400 hover:text-rose-600 shrink-0"><Trash2 size={13} /></button>
        </div>
      ))}
      <div className="flex items-center gap-1.5">
        <input
          className={INPUT}
          value={draft}
          placeholder={placeholder}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && draft.trim()) { e.preventDefault(); onChange([...items, draft.trim()]); setDraft('') } }}
        />
        <button
          onClick={() => { if (draft.trim()) { onChange([...items, draft.trim()]); setDraft('') } }}
          disabled={!draft.trim()}
          className="p-1.5 text-slate-400 hover:text-brand-600 disabled:opacity-30"
        ><Plus size={14} /></button>
      </div>
    </div>
  )
}

function Hidden() {
  return <span className="inline-flex items-center gap-1 text-slate-400"><Lock size={11} /> ซ่อนไว้</span>
}

function Loading() {
  return <div className="flex items-center gap-2 text-[12.5px] text-slate-400 py-1"><Loader2 size={13} className="animate-spin" /> กำลังโหลด…</div>
}
